import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Select } from 'antd'
import type { ReportDimension, ReportListQuery } from '@super-mega/server/contracts/analytics'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { ChartPanel } from './chart-panel.tsx'
import { REPORT_LABELS, REPORT_METRIC_LABELS, REPORT_VIEWS } from './const/reports.ts'
import type { ReportSearch } from './report-search.ts'
import { reportQueryOptions, reportScope } from './utils/report-query.ts'

export function ReportRanking({ dimension, search }: { dimension: ReportDimension; search: ReportSearch }) {
  const trpc = useTRPC()
  const view = REPORT_VIEWS[dimension]
  const [field, setField] = useState<ReportListQuery['sortField']>(view.rankField)
  const query = useQuery({
    ...trpc.analytics.list.queryOptions({
      ...reportScope(search, dimension),
      current: 1,
      pageSize: 10,
      sortField: field,
      sortOrder: 'desc',
      activeOnly: true
    }),
    ...reportQueryOptions
  })
  const rows = query.data?.data ?? []
  const money = ['billed', 'received', 'outstanding'].includes(field)
  if (query.isError) {
    return (
      <Alert
        type="error"
        showIcon
        title="排名加载失败"
        description={query.error.message}
        action={<Button onClick={() => void query.refetch()}>重试</Button>}
      />
    )
  }
  return (
    <ChartPanel
      title={
        field === view.rankField
          ? `${view.rankTitle} · 前十名`
          : `${REPORT_LABELS[dimension]}${REPORT_METRIC_LABELS[field]} · 前十名`
      }
      description={`在当前组合筛选范围内，按${REPORT_METRIC_LABELS[field]}从高到低排列${money ? '，金额单位为元' : ''}。`}
      extra={
        <Select
          aria-label="排名指标"
          className="w-36"
          value={field}
          onChange={setField}
          options={(['orders', 'billed', 'received', 'outstanding', 'activeRentals'] as const).map((key) => ({
            value: key,
            label: REPORT_METRIC_LABELS[key]
          }))}
        />
      }
      loading={query.isPending}
      empty={!rows.some((row) => Number(row[field]) !== 0)}
      option={{
        grid: { left: '2%', right: '12%', top: '8%', bottom: '3%', containLabel: true },
        xAxis: { type: 'value', axisLabel: { hideOverlap: true }, ...(money ? {} : { minInterval: 1 }) },
        yAxis: {
          type: 'category',
          inverse: true,
          data: rows.map((row) => row.name),
          axisLabel: { width: 120, overflow: 'truncate' }
        },
        media: [
          {
            query: { maxWidth: 360 },
            option: {
              grid: { right: '5%' },
              xAxis: {
                splitNumber: 2,
                axisLabel: {
                  hideOverlap: true,
                  formatter: (value: number) =>
                    new Intl.NumberFormat('zh-CN', {
                      notation: 'compact',
                      minimumFractionDigits: money ? 2 : 0,
                      maximumFractionDigits: money ? 2 : 1
                    }).format(value)
                }
              },
              yAxis: { axisLabel: { width: 70, overflow: 'truncate' } },
              tooltip: money ? { valueFormatter: formatAmount } : undefined,
              series: [{ label: { show: false } }]
            }
          },
          {
            option: {
              grid: { right: '12%' },
              xAxis: { splitNumber: 5, axisLabel: { hideOverlap: true, formatter: money ? formatAmount : '{value}' } },
              yAxis: { axisLabel: { width: 120, overflow: 'truncate' } },
              tooltip: money ? { valueFormatter: formatAmount } : undefined,
              series: [{ label: { show: true } }]
            }
          }
        ],
        tooltip: money ? { valueFormatter: formatAmount } : undefined,
        series: [
          {
            name: REPORT_METRIC_LABELS[field],
            type: 'bar',
            barMaxWidth: 18,
            data: rows.map((row) => Number(row[field])),
            itemStyle: { borderRadius: [0, 5, 5, 0] },
            label: {
              show: true,
              position: 'right',
              ...(money ? { formatter: (params: { value: unknown }) => formatAmount(params.value) } : {})
            }
          }
        ]
      }}
    />
  )
}
